import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "relative isolate inline-flex items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-[8px] text-sm font-semibold tracking-[-0.01em]",
    "ring-offset-background transition-[background-color,border-color,color,box-shadow,filter] duration-150 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "active:brightness-[0.96] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    // Keep the 220px grain crisp at high density without loading the original.
    "before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit] before:bg-[url('/_next/image?url=%2Fimages%2Fbg%2Fpaper_grain_texture_by_justinbhb_d7pfdwz-fullview.jpg&w=640&q=75')] before:bg-[length:220px_auto] before:opacity-[0.16] before:mix-blend-soft-light before:content-['']",
    "after:pointer-events-none after:absolute after:inset-x-0 after:top-0 after:h-px after:bg-white/35 after:content-['']",
    "dark:before:opacity-[0.1] dark:before:mix-blend-overlay dark:after:bg-white/20",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[0_1px_0_rgba(255,255,255,0.14)_inset,0_1px_2px_rgba(10,10,10,0.18)] hover:brightness-[0.97] dark:shadow-[0_1px_0_rgba(255,255,255,0.1)_inset,0_1px_2px_rgba(0,0,0,0.35)]",
        "default-dark":
          "border border-white/70 bg-white text-black shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_1px_2px_rgba(0,0,0,0.16)] hover:brightness-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-[0_1px_0_rgba(255,255,255,0.1)_inset,0_1px_2px_rgba(0,0,0,0.14)] hover:brightness-[0.97]",
        outline:
          "border border-border bg-background text-foreground shadow-[0_1px_0_rgba(255,255,255,0.9)_inset] before:opacity-[0.1] hover:bg-muted/70 dark:shadow-[0_1px_0_rgba(255,255,255,0.05)_inset]",
        "outline-dark":
          "border border-white/20 bg-black text-white shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_1px_2px_rgba(0,0,0,0.28)] hover:border-white/30 hover:bg-white/8",
        secondary:
          "border border-border bg-muted text-foreground shadow-[0_1px_0_rgba(255,255,255,0.84)_inset] before:opacity-[0.12] hover:bg-secondary dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset]",
        ghost:
          "border border-transparent bg-transparent text-foreground shadow-none before:opacity-[0.08] hover:bg-muted/80 hover:before:opacity-[0.12] active:bg-secondary",
        "ghost-dark":
          "border border-transparent bg-transparent text-white shadow-none before:opacity-[0.06] hover:border-white/15 hover:bg-white/8 hover:before:opacity-[0.1]",
        link: "border-transparent bg-transparent text-primary shadow-none before:hidden after:hidden hover:underline",
        ai: "bg-primary text-primary-foreground shadow-[0_1px_0_rgba(255,255,255,0.2)_inset,0_0_18px_rgba(0,200,83,0.18),0_1px_2px_rgba(10,10,10,0.18)] hover:brightness-[0.97] dark:shadow-[0_1px_0_rgba(255,255,255,0.12)_inset,0_0_18px_rgba(0,200,83,0.22),0_1px_2px_rgba(0,0,0,0.35)]",
        "ai-secondary":
          "border border-success/25 bg-background text-foreground shadow-[0_1px_0_rgba(255,255,255,0.9)_inset] before:opacity-[0.1] hover:border-success/40 hover:bg-success/[0.04] dark:shadow-[0_1px_0_rgba(255,255,255,0.05)_inset]",
      },
      size: {
        default: "h-11 px-4 py-2",
        sm: "h-10 px-3",
        lg: "h-12 px-8",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
